import { computeLastAcceptedPerDay } from '@/domain/conflict/conflictUtils';
import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';

import { PlankSelectionItem } from '../state/types/PlankSelectionStore';
import { Reserve } from '../types';

import {
  geefInzetVoorConflict,
  geefStelOfOorspronkelijkDienstVoorConflict,
  geefStelOfOorspronkelijkDienstVoorMancoConflict,
} from './conflict';
import { buildPlankSelectionKey } from './plankSelection';

/** Strips the elementen a planner already accepted, which is the view of a conflict the Plank
 * shows and therefore the one its label and werklijn are derived from. */
export function toOpenstaandConflict(conflict: Conflict): Conflict {
  return {
    ...conflict,
    elementen: conflict.elementen.filter((element) => !element.isGeaccepteerd),
    lastAcceptedPerDay: computeLastAcceptedPerDay(conflict),
  };
}

/** Maps an already-filtered (openstaand) conflict to its selection item, or undefined when it
 * has no werklijn to open. Split out so a caller that already computed the openstaand view
 * (e.g. ConflictListItem) does not have to filter twice. */
export function openstaandConflictToSelectionItem(conflict: Conflict): PlankSelectionItem | undefined {
  if (!conflict.werklijnId) return undefined;

  const element = conflict.type === 'MANCO' ? geefInzetVoorConflict(conflict) : conflict.elementen[0];
  const oorspronkelijkeDienst =
    conflict.type === 'MANCO'
      ? geefStelOfOorspronkelijkDienstVoorMancoConflict(element)
      : geefStelOfOorspronkelijkDienstVoorConflict(conflict);

  return {
    key: buildPlankSelectionKey('conflict', String(conflict.conflictId)),
    kind: 'conflict',
    sourceId: String(conflict.conflictId),
    label: `Conflict ${oorspronkelijkeDienst}`,
    werklijnId: conflict.werklijnId,
    eenheidNummer: conflict.eenheidNummer ?? undefined,
    materieelSoort: [conflict.eenheidSoort, conflict.eenheidAanduiding].filter(Boolean).join('-'),
    werklijnElementId: element?.werklijnElementId ?? undefined,
  };
}

/** Maps a raw conflict row to its selection item, applying the openstaand filtering itself so
 * a column and the rows it renders can never derive a different key or label for the same
 * conflict. Use `openstaandConflictToSelectionItem` directly when that filtering was already
 * done. */
export function conflictToSelectionItem(rawConflict: Conflict): PlankSelectionItem | undefined {
  return openstaandConflictToSelectionItem(toOpenstaandConflict(rawConflict));
}

export function defectToSelectionItem(defect: Defect): PlankSelectionItem {
  const sourceId = String(defect.werkorderId ?? defect.id ?? defect.eenheidNummer);

  return {
    key: buildPlankSelectionKey('defect', sourceId),
    kind: 'defect',
    sourceId,
    label: `Defect ${defect.eenheidNummer}`,
    eenheidNummer: defect.eenheidNummer,
    materieelSoort: defect.materieelSoort,
  };
}

export function reserveToSelectionItem(reserve: Reserve): PlankSelectionItem {
  return {
    key: buildPlankSelectionKey('reserve', reserve.id),
    kind: 'reserve',
    sourceId: reserve.id,
    label: `Reserve ${reserve.eenheid}`,
    eenheidNummer: reserve.eenheid,
    materieelSoort: reserve.materieelType,
  };
}
